/**
 * Maps dot-notation history field keys to human-readable labels.
 * Labels are sourced directly from BasicInfoForm/schema.js (which resolves
 * translations via the intl singleton at module load time).
 *
 * Used in HistoryModal to display friendly names instead of raw schema keys.
 */
import schema from '../containers/BasicInfoForm/schema';

// ─── Build label map from schema ──────────────────────────────────────────────

/**
 * Recursively walk the schema object. When we encounter an entry that has a
 * `label` string property it is a field definition — record it. Entries
 * without `label` are group/section containers; recurse into them.
 *
 * @param {object} obj   - current schema node
 * @param {string} prefix - dot-notation prefix accumulated so far
 * @param {object} map    - output map being built
 */
function buildLabelMap(obj, prefix, map) {
  if (!obj || typeof obj !== 'object') return;
  Object.entries(obj).forEach(([key, value]) => {
    if (!value || typeof value !== 'object') return;
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof value.label === 'string') {
      // Leaf field — record full path and also just the leaf key
      map[path] = value.label;
      map[key] = map[key] || value.label; // leaf key wins first definition
    } else {
      // Group — recurse
      buildLabelMap(value, path, map);
    }
  });
}

const LABEL_MAP = {};
// schema.basic_info contains all the field definitions
if (schema && schema.basic_info) {
  buildLabelMap(schema.basic_info, '', LABEL_MAP);
}

// Manual additions for upload fields and non-basic_info fields not in the schema
Object.assign(LABEL_MAP, {
  status:               'Status',
  photos:               'Images for QA',
  floorplans:           'Images for CAD',
  featured_upload:      'Storefront Image',
  floorplan_upload:     'Featured Floorplan',
  pdf_upload:           'LOD PDF',
  profile_pdf_upload:   'Profile PDF',
  autocad_upload:       'AutoCAD File',
});

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Convert a snake_case or dot-notation key to Title Case as fallback.
 * @param {string} key
 * @returns {string}
 */
function titleCase(key) {
  const leaf = key.includes('.') ? key.split('.').pop() : key;
  return leaf
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Return a human-readable label for a dot-notation history field key.
 *
 * Lookup order:
 *   1. Exact match in LABEL_MAP (after stripping "basic_info." prefix)
 *   2. Leaf key only
 *   3. Title-cased leaf as fallback
 *
 * @param {string} fieldName - dot-notation key (e.g. "basic_info.ceiling_information.clear_ceiling_height")
 * @returns {string}
 */
export function getFieldLabel(fieldName) {
  if (!fieldName) return fieldName;

  // Strip leading "basic_info." prefix
  const stripped = fieldName.startsWith('basic_info.')
    ? fieldName.slice('basic_info.'.length)
    : fieldName;

  // 1. Exact match (full path without basic_info prefix)
  if (LABEL_MAP[stripped]) return LABEL_MAP[stripped];

  // 2. Leaf key
  const leaf = stripped.includes('.') ? stripped.split('.').pop() : stripped;
  if (LABEL_MAP[leaf]) return LABEL_MAP[leaf];

  // 3. Fallback: title-case the leaf segment
  return titleCase(leaf);
}

/**
 * Return both label and raw key (for tooltip/debug display).
 * @param {string} fieldName
 * @returns {{ label: string, key: string }}
 */
export function getFieldLabelWithKey(fieldName) {
  return { label: getFieldLabel(fieldName), key: fieldName };
}
